import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Plus } from 'lucide-react';
import { createDefaultAction } from '../../config/actions';
import { ActionCard } from '../ActionCard';
import type { TriggerAction } from '../../types';

interface ActionsStepProps {
  actions: TriggerAction[];
  isActive: boolean;
  onChange: (actions: TriggerAction[]) => void;
  onActiveChange: (isActive: boolean) => void;
}

/**
 * ActionsStep
 * 
 * Шаг выбора действий в wizard
 * 
 * @returns {React.ReactElement} React компонент
 */
export function ActionsStep({
  actions,
  isActive,
  onChange,
  onActiveChange,
}: ActionsStepProps) {
  const addAction = () => {
    onChange([...actions, createDefaultAction('send_notification')]);
  };

  const updateAction = (index: number, action: TriggerAction) => {
    const updated = [...actions];
    updated[index] = action;
    onChange(updated);
  };

  const removeAction = (index: number) => {
    onChange(actions.filter((_, i) => i !== index));
  };

  return (
    <div className="grid gap-6 lg:grid-cols-3">
      <section className="space-y-4 lg:col-span-2">
        <div className="space-y-1">
          <h3 className="text-base font-semibold">Что делать?</h3>
          <p className="text-sm text-muted-foreground">
            Добавьте одно или несколько действий, которые выполнятся при срабатывании триггера.
            Действия выполняются последовательно.
          </p>
        </div>

        {actions.map((action, index) => (
          <ActionCard
            key={index}
            action={action}
            index={index}
            onChange={(updated) => updateAction(index, updated)}
            onRemove={() => removeAction(index)}
            canRemove={actions.length > 1}
          />
        ))}

        <Button type="button" variant="outline" onClick={addAction} className="w-full">
          <Plus className="h-4 w-4 mr-2" />
          Добавить действие
        </Button>
      </section>

      <section className="space-y-1 lg:col-span-1">
        <h3 className="text-base font-semibold">Настройки</h3>
        <div className="flex items-center space-x-2 pt-2">
          <Checkbox
            id="isActive"
            checked={isActive}
            onCheckedChange={(checked) => onActiveChange(checked === true)}
          />
          <label htmlFor="isActive" className="text-sm font-medium cursor-pointer">
            Триггер активен
          </label>
        </div>
        <p className="text-xs text-muted-foreground mt-1 ml-6">
          Неактивные триггеры не будут выполняться
        </p>
      </section>
    </div>
  );
}
